/**
 * Dropping issues onto the sidebar to assign them. dnd-kit only sees the
 * droppables inside each view's own DndContext, so while an issue is dragged
 * the pointer is hit-tested against sidebar items marked with
 * `data-sidebar-drop` instead.
 */
import { toast } from "sonner";
import { identifier } from "./format";
import { currentCycle } from "./hooks";
import { batch } from "./mutations";
import { getRow, getTable } from "./store";
import type { Id, Issue, Op } from "./types";
import { createStore, useStore } from "./ui";

export type DropTarget =
  | { kind: "project"; id: Id; label: string }
  /** The team's current cycle. */
  | { kind: "cycle"; teamId: Id; label: string }
  | { kind: "assignee"; id: Id; label: string };

export function targetKey(t: DropTarget): string {
  return t.kind === "cycle" ? `cycle:${t.teamId}` : `${t.kind}:${t.id}`;
}

const state = createStore<{ dragging: boolean; hover: string | null }>({ dragging: false, hover: null });
export const useSidebarDrop = () => useStore(state);

/** Props that make an element a drop target. */
export function dropTargetProps(t: DropTarget) {
  return { "data-sidebar-drop": JSON.stringify(t) };
}

function targetAt(x: number, y: number): DropTarget | null {
  // The drag overlay sits under the pointer, so look through every layer.
  for (const el of document.elementsFromPoint(x, y)) {
    const host = el.closest("[data-sidebar-drop]");
    if (!host) continue;
    try {
      return JSON.parse(host.getAttribute("data-sidebar-drop") ?? "") as DropTarget;
    } catch {
      return null;
    }
  }
  return null;
}

let pointer: { x: number; y: number } | null = null;
function onMove(e: PointerEvent) {
  pointer = { x: e.clientX, y: e.clientY };
  const t = targetAt(e.clientX, e.clientY);
  const hover = t ? targetKey(t) : null;
  if (hover !== state.get().hover) state.set((s) => ({ ...s, hover }));
}

/**
 * Call when an issue drag starts. The returned function ends tracking and
 * says which target, if any, is under the drop point (or, without one, the
 * last pointer position seen).
 */
export function startSidebarDrag(): (at?: { x: number; y: number } | null) => DropTarget | null {
  pointer = null;
  window.addEventListener("pointermove", onMove, { passive: true });
  state.set({ dragging: true, hover: null });
  return (at) => {
    window.removeEventListener("pointermove", onMove);
    const p = at ?? pointer;
    const t = p ? targetAt(p.x, p.y) : null;
    state.set({ dragging: false, hover: null });
    return t;
  };
}

/** Where a dnd-kit pointer drag ended: its start plus the distance moved. Keyboard drags have none. */
export function dropPoint(activatorEvent: Event | null, delta: { x: number; y: number }) {
  if (!activatorEvent || !("clientX" in activatorEvent)) return null;
  const e = activatorEvent as MouseEvent;
  return { x: e.clientX + delta.x, y: e.clientY + delta.y };
}

/** Assigns the issues to the target, with an Undo toast. */
export function dropIssues(t: DropTarget, issueIds: Id[]) {
  const issues = issueIds.map((id) => getRow("issue", id)).filter((i): i is Issue => !!i);
  if (!issues.length) return;
  const teams = getTable("team");
  let field: "projectId" | "cycleId" | "assigneeId";
  let value: Id;
  let done: (what: string) => string;
  let eligible = issues;
  if (t.kind === "cycle") {
    const cycle = currentCycle(Object.values(getTable("cycle")).filter((c) => c.teamId === t.teamId && !c.archivedAt));
    if (!cycle) return void toast.error(`${t.label} has no current cycle.`);
    eligible = issues.filter((i) => i.teamId === t.teamId);
    if (!eligible.length) return void toast.error(`Only ${t.label} issues can join its cycles.`);
    field = "cycleId";
    value = cycle.id;
    const name = cycle.name || `Cycle ${cycle.number}`;
    done = (what) => `Added ${what} to ${name}`;
  } else if (t.kind === "project") {
    field = "projectId";
    value = t.id;
    done = (what) => `Moved ${what} to ${t.label}`;
  } else {
    field = "assigneeId";
    value = t.id;
    done = (what) => `Assigned ${what} to ${t.label}`;
  }
  const changed = eligible.filter((i) => i[field] !== value);
  if (!changed.length) return void toast(issues.length === 1 ? "Nothing to change." : "Those issues are already there.");
  const before = changed.map((i) => ({ id: i.id, value: i[field] ?? null }));
  const ops = (pairs: { id: Id; value: Id | null }[]): Op[] => pairs.map((p) => ({ op: "update", entity: "issue", id: p.id, data: { [field]: p.value } }));
  batch(ops(changed.map((i) => ({ id: i.id, value })))).catch(() => {});
  const what = changed.length === 1 ? identifier(changed[0], teams) : `${changed.length} issues`;
  const skipped = issues.length - eligible.length;
  toast(done(what), {
    description: skipped ? `${skipped} from other teams stayed where they were.` : undefined,
    // A drop on the wrong row is easy to make, so leave time to undo it.
    duration: 10_000,
    action: { label: "Undo", onClick: () => void batch(ops(before)).catch(() => {}) },
  });
}
